import React, { useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { AlertCircle, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@cms/ui/components/card";
import { Alert, AlertTitle, AlertDescription } from "@cms/ui/components/alert";
import { PageHeader } from "@cms/ui/components/page-header";
import { useApiQuery } from "@cms/portal-lib/hooks";
import { formatPence } from "@cms/portal-lib/format";
import { cadenceLabel, cadencePeriodNoun } from "@cms/portal-lib/billing";
import { fetchBillingForecast, fetchBillingSummary } from "../lib/billing";
import { BillingForecastCard } from "../components/billing-forecast-card";
import { BillingTabs } from "../components/billing-tabs";
import type { ClientOutletContext } from "../lib/outlet-context";

/**
 * Billing → Plan: the full monthly breakdown — including discount lines the
 * Overview tab hides — plus the upcoming payment schedule.
 */
export function BillingPlanPage() {
  const { api, setBreadcrumbs } = useOutletContext<ClientOutletContext>();

  useEffect(() => {
    setBreadcrumbs?.([{ label: "Billing", href: "/billing" }, { label: "Plan" }]);
    return () => setBreadcrumbs?.(null);
  }, [setBreadcrumbs]);

  const summaryQuery = useApiQuery(() => (api ? fetchBillingSummary(api) : null), [api]);
  const forecastQuery = useApiQuery(() => (api ? fetchBillingForecast(api) : null), [api]);
  const summary = summaryQuery.data;

  return (
    <div>
      <BillingTabs />

      <PageHeader
        title="Plan"
        description="What makes up your bill, and your payments ahead."
      />

      {summaryQuery.error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Couldn't load billing</AlertTitle>
          <AlertDescription>{summaryQuery.error}</AlertDescription>
        </Alert>
      )}

      {!summary && !summaryQuery.error && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {summary && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Plan details</CardTitle>
            <CardDescription>
              Your {cadenceLabel(summary.billing_cadence).toLowerCase()} bill line by line,
              including any discounts.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="space-y-1 text-sm">
              {summary.lines.map((line) => (
                <div key={`${line.kind}-${line.slug ?? line.description}`} className="flex justify-between">
                  <dt className="text-muted-foreground">{line.description}</dt>
                  <dd>{formatPence(line.amount_pence)}</dd>
                </div>
              ))}
              {summary.vat_pence > 0 && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">VAT ({(summary.vat_rate_bps / 100).toFixed(0)}%)</dt>
                  <dd>{formatPence(summary.vat_pence)}</dd>
                </div>
              )}
              <div className="flex justify-between border-t pt-1 font-semibold">
                <dt>Total per {cadencePeriodNoun(summary.billing_cadence)}</dt>
                <dd>{formatPence(summary.total_pence)}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      )}

      {forecastQuery.data && <BillingForecastCard forecast={forecastQuery.data} />}
    </div>
  );
}
